import React, { useEffect, useState } from 'react';
import {
  Card,
  Table,
  Input,
  Select,
  DatePicker,
  Tag,
  Typography,
  Space,
  Row,
  Col,
  message,
} from 'antd';
import {
  SearchOutlined,
  FilterOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import api from '../../services/api';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

interface AuditLog {
  id: string;
  action_type: string;
  action_description: string;
  performed_by: string;
  performed_by_email: string;
  affected_entity_type: string;
  affected_entity_id: string;
  metadata: Record<string, any>;
  created_at: string;
}

const AuditLogsPage: React.FC = () => {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [actionTypeFilter, setActionTypeFilter] = useState<string>('');
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  useEffect(() => {
    fetchAuditLogs();
  }, [actionTypeFilter, dateRange, pagination.current, pagination.pageSize]);

  const fetchAuditLogs = async () => {
    try {
      setLoading(true);
      
      const params: any = {
        limit: pagination.pageSize,
        offset: (pagination.current - 1) * pagination.pageSize,
      };
      
      if (actionTypeFilter) {
        params.action_type = actionTypeFilter;
      }
      
      if (dateRange) {
        params.start_date = dateRange[0].toISOString();
        params.end_date = dateRange[1].toISOString();
      }
      
      const response = await api.get('/api/super-admin/audit-logs', { params });
      setLogs(response.data.logs || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.total || 0,
      }));
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (newPagination: any) => {
    setPagination({
      current: newPagination.current,
      pageSize: newPagination.pageSize,
      total: pagination.total,
    });
  };

  const filteredLogs = logs.filter(log => {
    if (!searchText) return true;
    const search = searchText.toLowerCase();
    return (
      (log.action_description || '').toLowerCase().includes(search) ||
      (log.performed_by_email || '').toLowerCase().includes(search) ||
      (log.affected_entity_id || '').toLowerCase().includes(search)
    );
  });

  const actionTypeColors: Record<string, string> = {
    'credit_plan_created': 'green',
    'credit_plan_updated': 'blue',
    'credit_plan_deleted': 'red',
    'api_key_generated': 'cyan',
    'api_key_revoked': 'orange',
    'webhook_config_updated': 'purple',
    'credits_adjusted': 'gold',
    'tenant_created': 'geekblue',
  };

  const columns: ColumnsType<AuditLog> = [
    {
      title: 'Timestamp',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (date: string) => (
        <div>
          <div>{dayjs(date).format('YYYY-MM-DD')}</div>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {dayjs(date).format('HH:mm:ss')}
          </Text>
        </div>
      ),
      sorter: (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Action',
      dataIndex: 'action_type',
      key: 'action_type',
      width: 200,
      render: (type: string) => (
        <Tag color={actionTypeColors[type] || 'default'}>
          {type?.replace(/_/g, ' ').toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'action_description',
      key: 'action_description',
      ellipsis: true,
    },
    {
      title: 'Performed By',
      key: 'performed_by',
      width: 200,
      render: (_, record) => (
        <div>
          <Text strong>{record.performed_by}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.performed_by_email}
          </Text>
        </div>
      ),
    },
    {
      title: 'Entity',
      key: 'entity',
      width: 180,
      render: (_, record) => (
        <div>
          <Tag>{record.affected_entity_type}</Tag>
          <br />
          <Text code style={{ fontSize: '11px' }}>
            {record.affected_entity_id?.substring(0, 16)}...
          </Text>
        </div>
      ),
    },
    {
      title: 'Metadata',
      dataIndex: 'metadata',
      key: 'metadata',
      width: 120,
      render: (metadata: Record<string, any>) => {
        if (!metadata || Object.keys(metadata).length === 0) {
          return <Text type="secondary">-</Text>;
        }
        
        return (
          <Space direction="vertical" size="small">
            {Object.entries(metadata).slice(0, 2).map(([key, value]) => (
              <div key={key} style={{ fontSize: '12px' }}>
                <Text type="secondary">{key}:</Text> {String(value)}
              </div>
            ))}
            {Object.keys(metadata).length > 2 && (
              <Text type="secondary" style={{ fontSize: '11px' }}>
                +{Object.keys(metadata).length - 2} more
              </Text>
            )}
          </Space>
        );
      },
    },
  ];

  const actionTypes = [
    'credit_plan_created',
    'credit_plan_updated',
    'credit_plan_deleted',
    'api_key_generated',
    'api_key_revoked',
    'webhook_config_updated',
    'credits_adjusted',
    'tenant_created',
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '24px' }}>
        <Title level={2}><HistoryOutlined /> Audit Logs</Title>
      </div>

      <Card style={{ marginBottom: '16px' }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8}>
            <Input
              placeholder="Search description, user, or entity..."
              prefix={<SearchOutlined />}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Select
              placeholder="Filter by action type"
              style={{ width: '100%' }}
              onChange={setActionTypeFilter}
              allowClear
              suffixIcon={<FilterOutlined />}
            >
              {actionTypes.map(type => (
                <Option key={type} value={type}>
                  {type.replace(/_/g, ' ').toUpperCase()}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <RangePicker
              style={{ width: '100%' }}
              onChange={(dates) => setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null)}
              format="YYYY-MM-DD"
            />
          </Col>
        </Row>
      </Card>

      <Card>
        <Table
          columns={columns}
          dataSource={filteredLogs}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} logs`,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
        />
      </Card>
    </div>
  );
};

export default AuditLogsPage;
